import { useHistory } from 'react-router-dom';
import useGetExtractionCard from 'graphQL/addContent/addStyle/hook';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  CardSchema,
  CardStyleSchema,
  CardTypeOptions,
  ECardExtractionOptions,
  ECardLayoutOptions,
} from 'constants/graphqlTypes';
import getImages from 'helpers/getImages';
import { useReports } from 'graphQL/cardOptions/hooks';
import { useSparkResponseLinks } from 'components/Sparks/Routes/AddResponseToSpark/hooks';
import { useAddContentLinks, useOfferReportLinks, useStyleItems } from '../helpers/hooks';
import { changeCardStyle, getPreExtractImage } from '../helpers/helpers';
import { IUseReportStylesData } from '../helpers/models';

const CARDV2_MEDIA_TYPES = ['book', 'movie', 'music', 'podcast'];

// @typescript-eslint/explicit-module-boundary-types
export const useAddStyleLogic = ({
  pickId,
  cardId,
  itemId,
  color,
  preExtractTitle,
  preExtractImage,
  setStyle,
  canonicalTitle,
  canonicalImageUrl,
  canonicalCreatorNames,
  canonicalMediaTagName,
}: {
  pickId: string;
  cardId: string;
  itemId: number;
  color: string;
  preExtractTitle?: string | null;
  preExtractImage?: string | null;
  setStyle: Dispatch<SetStateAction<CardStyleSchema | undefined>>;
  canonicalTitle?: string | null;
  canonicalImageUrl?: string | null;
  canonicalCreatorNames?: string[] | null;
  canonicalMediaTagName?: string | null;
}) => {
  const { card } = useGetExtractionCard(pickId);

  const [isPreExtraction, setIsPreExtraction] = useState(true);
  const { cardStyle, extractionStatus, mediaTags } = card ?? {};
  const { items, currentItem, itemColors, currentColor } = useStyleItems(itemId, color, card);

  const isExtracting = extractionStatus !== ECardExtractionOptions.Complete;
  const layout =
    card?.mediaTags?.items[0]?.name === 'video' ? ECardLayoutOptions.Borderless : ECardLayoutOptions.Framed;
  const updatedStyle = changeCardStyle(
    layout,
    cardStyle,
    currentColor,
    isPreExtraction && !currentItem?.image ? getPreExtractImage(preExtractImage) : currentItem?.image
  );
  const { imageUrl } = getImages(updatedStyle);

  // Build preview card, enriching with canonical data when extraction data isn't available yet
  const extractedMediaTagNames = card?.mediaTagNames ?? card?.mediaTags?.items?.map((t) => t?.name ?? '') ?? [];
  const canonicalFallbackTags = canonicalMediaTagName ? [canonicalMediaTagName] : [];
  const effectiveMediaTagNames = extractedMediaTagNames.length > 0 ? extractedMediaTagNames : canonicalFallbackTags;

  const previewCard: CardSchema = {
    id: pickId,
    cardId,
    type: CardTypeOptions.Card,
    ...card,
    title: card?.title || canonicalTitle || preExtractTitle,
    mediaTagNames: effectiveMediaTagNames,
    displayThumbnailUrl: card?.displayThumbnailUrl || canonicalImageUrl || null,
    displayCreatorNames:
      card?.displayCreatorNames && card.displayCreatorNames.length > 0
        ? card.displayCreatorNames
        : canonicalCreatorNames || [],
  };

  // Determine if CardV2 should be used based on media type
  const showCardV2 =
    previewCard.type === CardTypeOptions.Card &&
    effectiveMediaTagNames.some((tag) => CARDV2_MEDIA_TYPES.some((mt) => (tag ?? '').toLowerCase().includes(mt)));

  useEffect(() => {
    if (updatedStyle) {
      setIsPreExtraction(false);
      setStyle(updatedStyle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentColor, currentItem, mediaTags]);

  return {
    currentItem,
    isExtracting,
    currentColor,
    imageUrl,
    itemColors,
    updatedStyle,
    previewCard,
    items,
    showCardV2,
  };
};

export const useReportStyles = (variable: {
  cardId: string;
  reportComment?: string;
  type?: string;
  isSpark?: boolean;
}): IUseReportStylesData => {
  const { cardId, reportComment, type, isSpark } = variable;
  const { replace } = useHistory();
  const { addStyle } = useAddContentLinks();
  const { styleDescription, reportStyleIssue, reportStyleOther } = useOfferReportLinks();

  const {
    sparkResponseReportStyleIssue,
    sparkResponseStyleDescription,
    sparkResponseReportStyleOther,
    addResponseStyle,
  } = useSparkResponseLinks();

  const handleNavigateAfterReport = () => replace(isSpark ? addResponseStyle : addStyle);
  const { options, report } = useReports({ cardId, reportComment, type }, handleNavigateAfterReport);

  const handleSubmitReport = (id?: string) => {
    if (id) {
      report(id);
    } else {
      const questionId = options.find(({ title }) => title === 'Other')?.id ?? '';
      report(questionId);
    }
  };

  const handleReplaceModal = () => {
    if (type) {
      replace(isSpark ? sparkResponseReportStyleOther : reportStyleOther);
    } else {
      replace(isSpark ? sparkResponseReportStyleIssue : reportStyleIssue);
    }
  };

  return {
    options,
    handleSubmitReport,
    closeReportStyleModal: handleNavigateAfterReport,
    handleOpenDescription: () => replace(isSpark ? sparkResponseStyleDescription : styleDescription),
    handleReplaceModal,
  };
};
